// Project-canvas left rail — the SAME fixed glass-card shell + grouped rows as
// the /infra sidebar (InfraSidebar): a category FILTER for what shows on the
// canvas, an ENVIRONMENT picker, and PROJECT actions incl. a Publish button.
// (2026-06-17 per Nika "similar sidebar as we have on /infra on project canvas
// page, where we can filter stuff, switch environments, publish button etc".)
//
// Reactivity follows InfraSidebar's proven model: filter/env children read the
// driving state and return a NEW array each run (childrenAs re-evaluates), and
// active styling is an `.isActive` CONDITIONAL CLASS read fresh from root (paint
// factories freeze on a childrenAs in-place update).
// Bare shared-library import, not a deep components/_activeHighlight.js
// subpath — a per-file deep specifier into @symbo-ls/workspace-shared cannot
// resolve through the platform's public importmap (tickets/execution-board.md
// §1, same fix as workspace/app.js). Read through a call-time getter, not a
// module-scope destructure — `rowBase` below is a static object evaluated at
// import time, so an eager `_wsShared.components.activeHighlight` read here
// risks the same barrel-cycle-order crash the deferred-wrapper fix class
// exists to avoid (a5d0a2aa).
import _wsShared from '@symbo-ls/workspace-shared'
const getActiveHighlight = () => _wsShared.components.activeHighlight
import { CANVAS_RAIL } from '../globalScope.js'

const canvasActiveFilter = (s) => s.root?._canvasFilter || 'all'
const activeEnv = (s) => s.root?._canvasEnv || 'production'

// `label` on every FILTER_DEF holds a bundle KEY — ProjectCanvasFilterRow's
// `text: (el, s) => s.label` reads it off STATE, where neither a template nor a
// function value survives, so the row resolves it through polyglot instead.
const CANVAS_FILTER_DEFS = [
  { key: 'all', label: 'canvas.sidebar.filter.all', icon: 'grid' },
  { key: 'pages', label: 'canvas.sidebar.filter.pages', icon: 'copy' },
  { key: 'components', label: 'canvas.sidebar.filter.components', icon: 'library' },
  { key: 'ds', label: 'canvas.sidebar.filter.designSystem', icon: 'tree' }
]
const filterChildren = (el, s) => {
  const f = canvasActiveFilter(s)
  return CANVAS_FILTER_DEFS.map((d) => ({ ...d, _k: f }))
}

// `name` holds a bundle KEY — resolved by envName() / envMenuRows() below.
const ENV_DEFS = [
  { id: 'production', name: 'canvas.sidebar.env.production' },
  { id: 'staging', name: 'canvas.sidebar.env.staging' },
  { id: 'development', name: 'canvas.sidebar.env.development' }
]
// `name` on every ENV_DEF holds a bundle KEY (see ENV_DEFS above).
const envName = (s) =>
  ENV_DEFS.find((e) => e.id === activeEnv(s))?.name || 'canvas.sidebar.env.production'
// `el` is threaded in from the opener so the rows resolve at open time — the
// menu is transient, and CanvasCreateMenuItem reads `label`/`hint` off a value.
const canvasEnvMenuRows = (root, el) => {
  const rows = {}
  ENV_DEFS.forEach((env, i) => {
    rows[`Env${i}`] = {
      extends: 'CanvasCreateMenuItem',
      label: el.call('polyglot', env.name),
      hint: el.call(
        'polyglot',
        env.id === (root?._canvasEnv || 'production')
          ? 'canvas.sidebar.env.current'
          : 'canvas.sidebar.env.switchTo'
      ),
      iconName: 'server',
      onClick: (ev, it) => {
        it.call('closeDropdown')
        it.call('switchCanvasEnv', env.id)
      }
    }
  })
  return rows
}

// A nav row in the InfraSidebar idiom (SidebarRow + padding Z A / round C).
const item = (extra) => ({
  extends: 'SidebarRow',
  padding: 'Z A',
  round: 'radiusPill',
  fontSize: 'A',
  // Left-align the label — the sidebar inherits text-align:center from the
  // centered canvas overlay container, which would otherwise center the row
  // labels away from their leading icon (ProjectCanvasEnvPicker overrides the
  // same inheritance the same way).
  textAlign: 'left',
  MoreBtn: null,
  ...extra
})
const actionItem = (label, icon, onClick) => item({ icon, label, onClick })

const rowBase = {
  extends: 'SidebarRow',
  padding: 'Z A',
  round: 'radiusPill',
  fontSize: 'A',
  // Left-align the label against its leading icon — overrides the text-align:
  // center inherited from the centered canvas overlay container (same override
  // ProjectCanvasEnvPicker already applies).
  textAlign: 'left',
  MoreBtn: null,
  color: 'subtitle',
  // `hover` is the theme-mirrored interaction wash (DESIGN_LANGUAGE.md §4.3) —
  // proven to read in both themes here against this identical canvas-card-glass
  // theme, same as CanvasNavbar / CanvasMainNavbar.
  ':hover': { background: 'hover', color: 'title' },
  // House active/selected recipe — `selected` fill + title text, the SAME
  // `activeHighlight` AppNavbar's active route, the workspace switcher and the
  // settings rail all use (2026-08-02 per Nika "fix ui of selected navbar" —
  // the previous rgba(255,255,255,0.1) was a raw, non-token, non-theme-aware
  // value copy-pasted from /tickets + /infra rather than the house pattern;
  // `_activeHighlight.js` itself later moved off the near-opaque `overlayStrong`
  // wash per DESIGN_LANGUAGE.md §4.3 — this spread picks that up for free).
  // fontWeight is this row's own addition on top of the shared recipe.
  '.isActive': () => ({ ...getActiveHighlight()['.isActive'], fontWeight: '700' })
}

export const ProjectCanvasFilterRow = {
  ...rowBase,
  isActive: (el, s) => canvasActiveFilter(s) === s.key,
  Icon: {
    icon: (el, s) => s.icon,
    boxSize: 'A1 A1',
    color: 'caption',
    isActive: (el, s) => canvasActiveFilter(s) === s.key,
    '.isActive': { color: 'title' }
  },
  Label: {
    tag: 'span',
    fontSize: 'Z2',
    flex: '1',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => el.call('polyglot', s.label)
  },
  onClick: (e, el, s) => {
    e.preventDefault()
    el.call('setCanvasFilter', s.key)
  }
}

// PAGES list: the board pages (Figma-style, Nika 2026-08-01), each followed by
// the project's own pages whose tiles it holds, then the project pages on no
// board page (Nika 2026-10-31) — rows from canvasPagesGroupRows
// (functions/projectCanvasSidebar.js). A board page row switches the active
// page (swapping the board's item set); a project page row shows its tile.
const pageChildren = (el) => {
  const rows = el.call('canvasPagesGroupRows') || []
  // Trailing "Add page" row — a synthetic entry so it renders alongside the
  // dynamic pages under `childrenAs: 'state'` (a static sibling key is dropped
  // by the state-children reconcile). It holds every key the other rows hold:
  // a key a row lacks reads through to ROOT state.
  // Its words resolve HERE, in the session language: the row's Label reads
  // `s.name` off STATE (where neither a template nor a function value
  // survives), and a board page's `name` is saved data that must never pass
  // through polyglot. This builder runs in an effect, so the polyglot read
  // (root `lang`) re-runs it on a language switch.
  return [
    ...rows,
    {
      key: '__add',
      id: '__add',
      name: el.call('polyglot', 'canvas.sidebar.pages.add'),
      _active: false,
      _isAdd: true,
      _route: '',
      _nested: false
    }
  ]
}

export const ProjectCanvasPageRow = {
  ...rowBase,
  isActive: (el, s) => !!s._active,
  // A project page row under the board page that holds its tile indents so its
  // icon lines up with that board page's label: the row's own padding (A) +
  // icon box (A1) + icon-label gap (Z1), all in the row's em. Nesting reads
  // from the left padding alone — no guide line (design-direction.md §8).
  isNested: (el, s) => !!s._nested,
  '.isNested': { paddingLeft: 'calc(A + A1 + Z1)' },
  Icon: {
    // `'file'` is not a registered icon name (brand/designSystem icons — 553
    // entries, no `file`; verified live in this catalog and confirmed the
    // real canvas rail's page rows render the identical blank glyph, so this
    // is a real component bug, not a catalog artifact: fable CAT-5 #7's
    // "empty pill" is `Icon` painting nothing plus `Label.text: s.name`
    // reading empty state on the bare catalog mount, stacked in one cell).
    // `'document'` is the registered name for this exact page/file glyph.
    // A project page row (`_route`) takes `'copy'` — the glyph of the FILTER
    // group's "Pages" row, the same project-page bucket.
    icon: (el, s) => (s._isAdd ? 'plus' : s._route ? 'copy' : 'document'),
    boxSize: 'A1 A1',
    color: 'caption',
    isActive: (el, s) => !!s._active,
    '.isActive': { color: 'title' }
  },
  Label: {
    tag: 'span',
    fontSize: 'Z2',
    flex: '1',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => s.name,
    // Swaps out for the inline rename input. Reactive `display:` predicate —
    // NOT `hide:` (a mounted-hidden element short-circuits and never comes
    // back) and not a `style: {}` wrapper (FA108: display is a flat CSS prop).
    display: (el, s) => (s.root._canvasPageRenaming === s.id ? 'none' : 'block')
  },
  // Inline rename editor. Shown only for the row being renamed; seeds + focuses
  // itself on the render the flag triggers, so no lookup/querySelector reach-in.
  RenameInput: {
    tag: 'input',
    type: 'text',
    flex: '1',
    minWidth: '0',
    fontSize: 'Z2',
    color: 'title',
    background: 'overlayStrong',
    borderWidth: '0',
    round: 'radiusControl',
    padding: 'V Y',
    // Reveal + focus are scheduled from THIS factory rather than an `onRender`
    // hook: v3.14 fires onRender on the initial render only, so the flag flip
    // that reveals the editor never ran it and the field opened unfocused. The
    // factory re-runs on every state change, and the microtask defers the
    // focus until after this display value has actually been applied (focusing
    // a still-`display: none` node is a silent no-op). Guarded on
    // activeElement so re-renders while typing never steal the caret back to a
    // full selection.
    display: (el, s) => {
      const renaming = s.root._canvasPageRenaming === s.id
      if (renaming) {
        queueMicrotask(() => {
          if (!el.node || s.root._canvasPageRenaming !== s.id) return
          if (typeof document !== 'undefined' && document.activeElement === el.node) return
          el.node.focus()
          el.node.select()
        })
      }
      return renaming ? 'block' : 'none'
    },
    // Controlled by state, never `el.node.value = …` (FA511). The draft lives in
    // `_canvasPageRenameDraft` so a re-render mid-edit re-renders what the user
    // typed instead of resetting the field to the page's stored name.
    value: (el, s) =>
      s.root._canvasPageRenaming === s.id ? s.root._canvasPageRenameDraft || '' : s.name || '',
    onInput: (e, el) => el.call('setBoardPageRenameDraft', el.node.value),
    // Keep the row's click (switch page) from firing while editing.
    onClick: (e) => e.stopPropagation(),
    onPointerdown: (e) => e.stopPropagation(),
    onKeydown: (e, el, s) => {
      e.stopPropagation()
      if (e.key === 'Enter') el.call('commitRenameBoardPage', s.id)
      else if (e.key === 'Escape') el.call('cancelRenameBoardPage')
    },
    onBlur: (e, el, s) => {
      if (s.root._canvasPageRenaming === s.id) el.call('commitRenameBoardPage', s.id)
    }
  },
  // A project page row shows its tile: opens the board page holding it, then
  // centres + selects it (focusProjectPage).
  onClick: (e, el, s) => {
    e.preventDefault()
    if (s._isAdd) el.call('addBoardPage')
    else if (s._route) el.call('focusProjectPage', s._route)
    else el.call('switchBoardPage', s.id)
  },
  onContextmenu: (e, el, s) => el.call('boardPageContextmenu', e, s)
}

// ── ITEMS — the entity (data-item) nodes on the ACTIVE board page ──────────
// Spec §3.4. Reads the same `boardEntityItems()` list the AI attachment and the
// expander use, so the rail can never disagree with what is on the board. Click
// centres the node at the live zoom and selects it (focusCanvasEntity), which
// is the same action the assistant's source chips fire.
const entityChildren = (el) => {
  const rows = el.call('boardEntityItems') || []
  const v2 = el.call('getCanvasV2')
  const primary = (v2 && v2.selection && v2.selection.primary) || null
  return rows.map((r) => ({ ...r, _active: r.key === primary }))
}

export const ProjectCanvasEntityRow = {
  ...rowBase,
  isActive: (el, s) => !!s._active,
  Icon: {
    icon: (el, s) => s.icon || 'document',
    boxSize: 'A1 A1',
    color: 'caption',
    isActive: (el, s) => !!s._active,
    '.isActive': { color: 'title' }
  },
  Label: {
    tag: 'span',
    fontSize: 'Z2',
    flex: '1',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    // A resolved entity title, or the bare ref while the card is still
    // loading. A DATA value either way — never routed through polyglot.
    text: (el, s) => s.title
  },
  onClick: (e, el, s) => {
    e.preventDefault()
    el.call('focusCanvasEntity', s.key)
  }
}

export const ProjectCanvasEnvPicker = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center left',
  gap: 'Z1',
  width: '100%',
  padding: 'Z A',
  round: 'radiusPill',
  fontSize: 'A',
  cursor: 'pointer',
  borderWidth: '0',
  textAlign: 'left',
  background: 'transparent',
  color: 'subtitle',
  ':hover': { background: 'hover', color: 'title' },
  // Press and keyboard focus take the SidebarRow states (workspace
  // components/SidebarRow.js), so the picker reads like the rows beside it.
  ':active': { background: 'selected', color: 'title' },
  ':focus-visible': { background: 'selected', color: 'title' },
  subscribeTo: ['_canvasEnv'],
  Dot: { boxSize: 'X', minWidth: 'X', round: '100%', background: 'caption' },
  Label: {
    tag: 'span',
    flex: '1',
    fontSize: 'Z2',
    fontWeight: '700',
    color: 'title',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    text: (el, s) => el.call('polyglot', envName(s))
  },
  Chevron: { extends: 'Icon', name: 'chevronDown', boxSize: 'Z1', color: 'caption' },
  onClick: (e, el, s) => {
    e.preventDefault()
    e.stopPropagation()
    el.call('openDropdown', {
      id: 'projectCanvasEnv',
      anchorEl: el,
      placement: 'bottom-start',
      content: {
        extends: 'CanvasCreateMenuBody',
        aria: { label: (el) => el.call('polyglot', 'canvas.sidebar.env.group') },
        ...canvasEnvMenuRows(s.root, el)
      }
    })
  }
}

export const ProjectCanvasSidebar = {
  // THE PAGE-SIDEBAR RULE (Nika 2026-10-29: "… move sidebar at the right - if
  // navbar is at the left, and opposite … make them consistent and make them
  // toggleable"; the pick "Hide fully, remember per page"; 21:42 "Same rule
  // for all"): the rail is the shared AppSidebar column — the page-nav mark,
  // the side rule, the one hide button in its card's corner and the reopen
  // tab (shared functions/pageSidebar.js). Its own x, the left-edge reopen
  // island and the session flag `_sidebarsHidden` that drove them are gone.
  // The project page opts its DESKTOP mount in (pages/main.js); the phone
  // sheet shows the same rail inline.
  extends: 'AppSidebar',
  position: 'fixed',
  // Clear of a drawer the assistant docks on the left, as the mirror below
  // clears one on the right (`--assistant-inset-left`).
  left: 'calc(var(--assistant-inset-left, calc(var(--spacing-A) * 0)) + var(--spacing-X2))',
  // THE MIRROR (the infra rail's form — infra components/InfraSidebar.js):
  // beside a left navbar the rail takes the right edge, clear of a drawer the
  // assistant docks there (`--assistant-inset-right`, zero when nothing is
  // docked or outside the shell — the token-only zero fallback). The column
  // hosts one card and no chrome, so the shared mirror's turned padding and
  // its step over the AppMain gutter stay off.
  '&[data-sidebar-side="right"]': {
    left: 'auto',
    right: 'calc(var(--assistant-inset-right, calc(var(--spacing-A) * 0)) + var(--spacing-X2))',
    padding: '0',
    marginLeft: '0',
    '@mobileL': { padding: '0' },
    '@appMobile': { padding: '0' }
  },
  // --shell-top — the shared canvas-rail top inset (px-pinned via @property
  // in app.js); was a stale 'D1' ~16px below every other rail. (2026-07-06
  // per Nika "make sure sidebar positions are consistent".)
  top: '--shell-top',
  width: 'G1',
  minWidth: 'G1',
  maxWidth: 'G1',
  // S4 (tickets/sonnet.md, NIKA BATCH 2026-08-16): was a hardcoded
  // `calc(100vh - 120px)` approximating the composer's live clearance.
  // Bottom now tracks the SAME --slot-bottom CanvasPageSlot itself uses.
  bottom: '--slot-bottom',
  // Docked rail band — below the platform AppHeader on purpose (was 1200,
  // which outranked it; see globalScope.js's "Canvas layering contract",
  // Nika 2026-08-02).
  zIndex: CANVAS_RAIL,
  // A column of one card (no chrome on the column itself): the card carries
  // the glass, the column only places it.
  flow: 'y',
  padding: '0',
  // Match TicketsSidebar / InfraSidebar scale (em-based tokens otherwise render
  // ~1.2× bigger off a plain object root).
  fontSize: 'Z',
  '@mobileL': { padding: '0', overflow: 'visible' },
  // A narrow app surface keeps the rail's own fixed posture (the shared
  // column's narrow rule would make a fixed rail the viewport's width).
  '@appMobile': {
    width: 'G1',
    minWidth: 'G1',
    maxWidth: 'G1',
    maxHeight: 'none',
    padding: '0',
    overflow: 'visible'
  },

  // No view switcher on the project canvas (the shared one hides itself off
  // its own routes; null says so here).
  ViewSwitcher: null,

  // THE HIDE BUTTON: one Y inside the card's corner on both sides (the shared
  // offsets add a column's padding; this column has none). The card's first
  // row is the PAGES caption, so the corner is free.
  SidebarToggle: {
    top: 'Y',
    right: 'Y',
    '&[data-side="right"]': { right: 'Y' }
  },

  // ── The rail's one card: the glass, and the groups inside it ──
  // The surface is the column's sidebar card (shared AppSidebar childExtends
  // AppSidebarCard, Nika 2026-10-30 "A: navbar glass").
  RailCard: {
    // hidden (was auto): the whole card used to scroll, so on any project with
    // more than ~a dozen pages the ENVIRONMENT + PROJECT groups (Preview /
    // Version history / Publish) sat entirely below the fold with zero
    // affordance — users never saw them at all (2026-08-08 per Nika "add
    // publish, preview in left sidebar" — they were already here, just buried).
    // Now PAGES is the internal scroll region and the action groups stay
    // pinned; see PagesGroup below.
    overflow: 'hidden',
    flow: 'y',
    minHeight: '0',

    // ── PAGES — Figma-style board pages (Nika 2026-08-01) + the project's own
    // pages under the board page holding each tile (Nika 2026-10-31) ──
    PagesGroup: {
      extends: 'AppSidebarGroup',
      label: (el) => el.call('polyglot', 'canvas.sidebar.group.pages'),
      // THE scroll region: the card is content-height (it hugs its groups), so
      // pages get a BOUNDED viewport-share and scroll internally — the groups
      // after them (FILTER / ENVIRONMENT / PROJECT with Preview / Version
      // history / Publish) always stay on screen. A pure flex:1 collapses here
      // (auto-height flex parent → basis 0 → ~2 rows), hence the vh bound.
      List: {
        overflow: 'auto',
        maxHeight: '34vh',
        flow: 'y',
        gap: 'V',
        // `_canvasPageRenaming` too — entering/leaving rename mode must re-render
        // the rows so the Label↔RenameInput display factories re-evaluate.
        // `data` — the project's pages (root.data.pages) change on an AI write,
        // a manual add and a remote rename or delete. `_cvSelectionTick` — a
        // project page row highlights while its tile is the primary selection,
        // and a selection write mutates canvas.selection in place, bumping only
        // this tick (functions/canvas/selection.js).
        subscribeTo: ['canvas', '_canvasPageRenaming', 'data', '_cvSelectionTick'],
        childExtends: 'ProjectCanvasPageRow',
        children: pageChildren,
        childrenAs: 'state'
      }
    },

    // ── ITEMS — entity (data-item) nodes on this board page (spec §3.4) ──
    // Mounted ALWAYS but reactively hidden while the board has none, so a normal
    // project canvas looks exactly as it did (`hide` is the one prop that
    // reactively toggles in v3.14; `subscribeTo: ['canvas']` wakes it when a node
    // is added or a board page is switched).
    ItemsGroup: {
      extends: 'AppSidebarGroup',
      label: (el) => el.call('polyglot', 'canvas.entity.group.items'),
      subscribeTo: ['canvas'],
      hide: (el) => !(el.call('boardEntityItems') || []).length,
      List: {
        overflow: 'auto',
        maxHeight: '24vh',
        flow: 'y',
        gap: 'V',
        subscribeTo: ['canvas'],
        childExtends: 'ProjectCanvasEntityRow',
        children: entityChildren,
        childrenAs: 'state'
      }
    },

    // ── FILTER — what shows on the canvas ──
    FilterGroup: {
      extends: 'AppSidebarGroup',
      label: (el) => el.call('polyglot', 'canvas.sidebar.group.filter'),
      List: {
        flow: 'y',
        gap: 'V',
        subscribeTo: ['_canvasFilter'],
        childExtends: 'ProjectCanvasFilterRow',
        children: filterChildren,
        childrenAs: 'state'
      }
    },

    // ── ENVIRONMENT — a select picker ──
    EnvGroup: {
      extends: 'AppSidebarGroup',
      label: (el) => el.call('polyglot', 'canvas.sidebar.group.environment'),
      List: {
        flow: 'y',
        gap: 'V',
        Picker: { extends: 'ProjectCanvasEnvPicker' }
      }
    },

    // ── PROJECT — actions + Publish ──
    ProjectGroup: {
      extends: 'AppSidebarGroup',
      label: (el) => el.call('polyglot', 'canvas.sidebar.group.project'),
      List: {
        flow: 'y',
        gap: 'V',
        Preview: actionItem(
          (el) => el.call('polyglot', 'canvas.sidebar.action.preview'),
          'eye',
          (e, el) => {
            e.preventDefault()
            el.call('openProjectPreviewPane')
          }
        ),
        Settings: actionItem(
          (el) => el.call('polyglot', 'canvas.sidebar.action.projectSettings'),
          'settings',
          (e, el, s) => {
            e.preventDefault()
            s.root.update({ settingsTab: 'project', _canvasRailOpen: false })
            el.call('openCustomModal', {
              id: 'settings',
              width: 'overlayLg',
              content: { extends: 'SettingsModalBody' }
            })
          }
        ),
        // Version history rail — list / preview / restore project versions (the
        // legacy platform's Versions aside, rebuilt — VersionHistoryPanel).
        Versions: actionItem(
          (el) => el.call('polyglot', 'canvas.sidebar.action.versionHistory'),
          'clockOutline',
          (e, el) => {
            e.preventDefault()
            el.call('toggleVersionHistory')
          }
        ),
        // Prominent publish button → the publish modal (shows the diff + publishes
        // to staging/production; the `smbls publish` equivalent).
        PublishBtn: {
          extends: 'Button',
          theme: 'primary',
          type: 'button',
          width: '100%',
          margin: 'V 0 0',
          text: '{{ canvas.sidebar.action.publish | polyglot }}',
          onClick: (e, el) => {
            e.preventDefault()
            el.call('openPublishModal')
          }
        }
      }
    }
  }
}
